// SIAP Monitoring Dashboard functionality
class SiapMonitoring {
  constructor() {
    this.refreshInterval = null
    this.autoRefreshIntervalMs = 30000 // 30 seconds
    this.charts = {}
    this.init()
  }

  init() {
    this.bindEvents()
    this.initializeCharts()
    this.startAutoRefresh()
  }

  bindEvents() {
    // Refresh All button
    const refreshAllBtn = document.querySelector('[data-action="siap-monitoring#refreshAll"]')
    if (refreshAllBtn) {
      refreshAllBtn.addEventListener('click', () => this.refreshAll())
    }

    // Refresh Errors button
    const refreshErrorsBtn = document.querySelector('[data-action="siap-monitoring#refreshErrors"]')
    if (refreshErrorsBtn) {
      refreshErrorsBtn.addEventListener('click', () => this.refreshErrors())
    }

    // Period change buttons
    const periodButtons = document.querySelectorAll('[data-action="siap-monitoring#changePeriod"]')
    periodButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.changePeriod(e))
    })

    // Audit form handling
    const auditForm = document.querySelector('[data-siap-monitoring-target="auditForm"]')
    if (auditForm) {
      auditForm.addEventListener('submit', (e) => {
        e.preventDefault()
        this.submitAuditForm()
      })
    }

    // Enhanced collapse toggles for all sections
    this.bindCollapseToggles()

    // View buttons in audit logs table
    this.bindViewButtons()
  }

  initializeCharts() {
    if (typeof Chart !== 'undefined' && window.siapChartData) {
      this.initializeDurationChart()
      this.initializeRecordsChart()
    }
  }

  initializeDurationChart() {
    const canvas = document.getElementById('durationChart')
    if (canvas && window.siapChartData?.duration) {
      const ctx = canvas.getContext('2d')
      
      this.charts.duration = new Chart(ctx, {
        type: 'line',
        data: window.siapChartData.duration,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Sync Duration Over Time'
            },
            legend: {
              display: true,
              position: 'top'
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Time'
              }
            },
            y: {
              display: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Duration (seconds)'
              }
            }
          }
        }
      })
    }
  }

  initializeRecordsChart() {
    const canvas = document.getElementById('recordsChart')
    if (canvas && window.siapChartData?.records) {
      const ctx = canvas.getContext('2d')
      
      this.charts.records = new Chart(ctx, {
        type: 'bar',
        data: window.siapChartData.records,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Records Processed by Entity Type'
            },
            legend: {
              display: true,
              position: 'top'
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Time'
              }
            },
            y: {
              display: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Records Count'
              }
            }
          }
        }
      })
    }
  }

  startAutoRefresh() {
    this.stopAutoRefresh()
    
    this.refreshInterval = setInterval(() => {
      this.refreshAll()
    }, this.autoRefreshIntervalMs)

    console.log(`Auto-refresh started (${this.autoRefreshIntervalMs}ms)`)
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
      console.log("Auto-refresh stopped")
    }
  }

  async refreshAll() {
    console.log("Refreshing all dashboard components...")
    
    try {
      const response = await fetch('/admin/siap_monitoring/refresh_status', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateDashboardData(data)
        this.showToast('Dashboard refreshed successfully', 'success')
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error refreshing dashboard:', error)
      this.showToast(`Failed to refresh dashboard: ${error.message}`, 'error')
    }
  }

  async refreshErrors() {
    try {
      const response = await fetch('/admin/siap_monitoring/refresh_status?component=errors', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateErrorsContainer(data.recent_errors || [])
      }
    } catch (error) {
      console.error('Error refreshing errors:', error)
    }
  }

  async changePeriod(event) {
    const period = event.currentTarget.dataset.siapMonitoringPeriodParam
    const chartType = event.currentTarget.dataset.siapMonitoringChartParam
    
    // Update button states
    const buttonGroup = event.currentTarget.parentElement
    buttonGroup.querySelectorAll('.btn').forEach(btn => btn.classList.remove('active'))
    event.currentTarget.classList.add('active')

    try {
      const response = await fetch(`/admin/siap_monitoring/metrics?type=${chartType === 'duration' ? 'sync_duration' : 'records_processed'}&period=${period}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateChart(chartType, data)
      }
    } catch (error) {
      console.error(`Error updating ${chartType} chart:`, error)
    }
  }

  updateChart(chartType, data) {
    const chart = this.charts[chartType]
    
    if (chart) {
      chart.data = data
      chart.update('none')
    }
  }

  updateDashboardData(data) {
    // Update sync status card
    if (data.sync_status) {
      this.updateSyncStatusCard(data.sync_status)
    }

    // Update circuit breaker card
    if (data.circuit_breaker_status) {
      this.updateCircuitBreakerCard(data.circuit_breaker_status)
    }

    // Update lock status card
    if (data.lock_status) {
      this.updateLockStatusCard(data.lock_status)
    }

    // Update error rate card
    if (data.error_metrics) {
      this.updateErrorRateCard(data.error_metrics)
    }

    // Update errors container
    if (data.error_metrics?.recent_errors) {
      this.updateErrorsContainer(data.error_metrics.recent_errors)
    }
  }

  updateSyncStatusCard(syncStatus) {
    const card = document.querySelector('[data-target="siap-monitoring.syncStatusCard"]')
    if (!card) return
    
    const statusElement = card.querySelector('h4')
    const detailElement = card.querySelector('small')
    
    if (statusElement) {
      statusElement.textContent = this.humanize(syncStatus.current_status)
      statusElement.className = `mb-1 ${this.getSyncStatusClass(syncStatus.current_status)}`
    }
    
    if (detailElement) {
      detailElement.textContent = syncStatus.current_status_detail || 'No details available'
    }
  }

  updateCircuitBreakerCard(circuitBreakerStatus) {
    const card = document.querySelector('[data-target="siap-monitoring.circuitBreakerCard"]')
    if (!card) return
    
    const statusElement = card.querySelector('h4')
    const detailElement = card.querySelector('small')
    
    if (statusElement) {
      statusElement.textContent = this.humanize(circuitBreakerStatus.state)
      statusElement.className = `mb-1 ${this.getCircuitBreakerStatusClass(circuitBreakerStatus.state)}`
    }
    
    if (detailElement && circuitBreakerStatus.health_percentage !== undefined) {
      detailElement.textContent = `Health: ${circuitBreakerStatus.health_percentage}%`
    }
  }

  updateLockStatusCard(lockStatus) {
    const card = document.querySelector('[data-target="siap-monitoring.lockStatusCard"]')
    if (!card) return
    
    const statusElement = card.querySelector('h4')
    const detailElement = card.querySelector('small')
    
    if (statusElement) {
      statusElement.textContent = lockStatus.locked ? 'Locked' : 'Available'
      statusElement.className = `mb-1 ${this.getLockStatusClass(lockStatus.locked)}`
    }
    
    if (detailElement) {
      detailElement.textContent = lockStatus.locked ? 'Sync in progress' : 'Ready for sync'
    }
  }

  updateErrorRateCard(errorMetrics) {
    const card = document.querySelector('[data-target="siap-monitoring.errorRateCard"]')
    if (!card) return
    
    const statusElement = card.querySelector('h4')
    const detailElement = card.querySelector('small')
    
    if (statusElement && errorMetrics.error_rate !== undefined) {
      statusElement.textContent = `${errorMetrics.error_rate}%`
      statusElement.className = `mb-1 ${this.getErrorRateClass(errorMetrics.error_rate)}`
    }
    
    if (detailElement && errorMetrics.failed_sync_attempts !== undefined) {
      detailElement.textContent = `${errorMetrics.failed_sync_attempts} failed attempts`
    }
  }

  updateErrorsContainer(errors) {
    const container = document.querySelector('[data-target="siap-monitoring.errorsContainer"]')
    if (!container || !Array.isArray(errors)) return
    
    let html = ''
    
    if (errors.length > 0) {
      errors.forEach(error => {
        html += `
          <div class="alert alert-danger alert-dismissible fade show">
            <strong>${error.timestamp}</strong>
            <p class="mb-0">${this.escapeHtml(error.message)}</p>
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
          </div>
        `
      })
    } else {
      html = `
        <div class="text-center py-4">
          <i class="fas fa-check-circle text-success fs-1 mb-2"></i>
          <p class="text-muted">No recent errors</p>
        </div>
      `
    }
    
    container.innerHTML = html
  }

  async submitAuditForm() {
    const form = document.querySelector('[data-siap-monitoring-target="auditForm"]')
    const container = document.querySelector('[data-target="siap-monitoring.auditLogsContainer"]')
    
    if (!form || !container) return
    
    const formData = new FormData(form)
    const params = new URLSearchParams(formData)
    
    try {
      const response = await fetch(`/admin/siap_monitoring/audit_logs?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (response.ok) {
        const html = await response.text()
        container.innerHTML = html
        // Re-bind view buttons after updating the content
        this.bindViewButtons()
      }
    } catch (error) {
      console.error('Error filtering audit logs:', error)
    }
  }

  bindCollapseToggles() {
    // Performance section toggle
    const performanceToggle = document.querySelector('[data-bs-toggle="collapse"][data-bs-target="#performanceSection"]')
    if (performanceToggle) {
      performanceToggle.addEventListener('click', () => {
        this.toggleCollapsibleSection('#performanceSection', '#toggleChartsText', 'Show Charts', 'Hide Charts')
      })
    }

    // Data section toggle
    const dataToggle = document.querySelector('[data-bs-toggle="collapse"][data-bs-target="#dataSection"]')
    if (dataToggle) {
      dataToggle.addEventListener('click', () => {
        this.toggleCollapsibleSection('#dataSection', '#toggleDataText', 'Show Details', 'Hide Details')
      })
    }

    // Errors section toggle
    const errorsToggle = document.querySelector('[data-bs-toggle="collapse"][data-bs-target="#errorsSection"]')
    if (errorsToggle) {
      errorsToggle.addEventListener('click', () => {
        this.toggleCollapsibleSection('#errorsSection', '#toggleErrorsText', 'Show Details', 'Hide Details')
      })
    }

    // Audit log filters toggle
    const filtersToggle = document.querySelector('[data-bs-toggle="collapse"][data-bs-target="#auditLogFilters"]')
    if (filtersToggle) {
      filtersToggle.addEventListener('click', () => {
        this.toggleCollapsibleSection('#auditLogFilters', null, null, null)
      })
    }
  }

  toggleCollapsibleSection(sectionId, textElementId, showText, hideText) {
    const section = document.querySelector(sectionId)
    const textElement = textElementId ? document.querySelector(textElementId) : null
    
    if (!section) return

    const isCurrentlyVisible = section.classList.contains('show')
    
    if (isCurrentlyVisible) {
      // Hide section
      section.style.height = section.scrollHeight + 'px'
      section.offsetHeight // Force reflow
      section.classList.add('collapsing')
      section.classList.remove('collapse', 'show')
      section.style.height = '0'
      
      setTimeout(() => {
        section.classList.remove('collapsing')
        section.classList.add('collapse')
        section.style.height = ''
      }, 350)
      
      if (textElement && showText) {
        textElement.textContent = showText
      }
    } else {
      // Show section
      section.classList.remove('collapse')
      section.classList.add('collapsing')
      section.style.height = section.scrollHeight + 'px'
      
      setTimeout(() => {
        section.classList.remove('collapsing')
        section.classList.add('collapse', 'show')
        section.style.height = ''
      }, 350)
      
      if (textElement && hideText) {
        textElement.textContent = hideText
      }
    }
  }

  bindViewButtons() {
    // Remove existing listeners to prevent duplicates
    const viewButtons = document.querySelectorAll('button[data-bs-toggle="modal"][data-bs-target="#logDetailModal"]')
    viewButtons.forEach(btn => {
      btn.replaceWith(btn.cloneNode(true)) // Remove all event listeners
    })

    // Re-bind new listeners
    document.querySelectorAll('button[data-bs-toggle="modal"][data-bs-target="#logDetailModal"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.showLogDetails(e))
    })
  }

  async showLogDetails(event) {
    const logId = event.currentTarget.dataset.logId
    if (!logId) {
      console.error('No log ID found')
      return
    }

    // Show modal (basic implementation without Bootstrap JS)
    let modal = document.getElementById('logDetailModal')
    if (!modal) {
      // Create modal if it doesn't exist
      modal = this.createLogDetailModal()
    }

    const modalContent = modal.querySelector('#logDetailContent')
    if (modalContent) {
      modalContent.textContent = 'Loading...'
    }

    // Show the modal
    modal.style.display = 'block'
    modal.classList.add('show')
    document.body.classList.add('modal-open')

    try {
      // For now, just show basic log info since we don't have a specific endpoint
      // You can enhance this to fetch detailed log information
      const logRow = event.currentTarget.closest('tr')
      if (logRow) {
        const cells = logRow.querySelectorAll('td')
        const logInfo = {
          timestamp: cells[0]?.textContent?.trim(),
          action: cells[1]?.textContent?.trim(),
          entityType: cells[2]?.textContent?.trim(),
          status: cells[3]?.textContent?.trim(),
          details: cells[4]?.textContent?.trim(),
          duration: cells[5]?.textContent?.trim()
        }

        modalContent.innerHTML = `
          <strong>Timestamp:</strong> ${logInfo.timestamp || 'N/A'}<br>
          <strong>Action:</strong> ${logInfo.action || 'N/A'}<br>
          <strong>Entity Type:</strong> ${logInfo.entityType || 'N/A'}<br>
          <strong>Status:</strong> ${logInfo.status || 'N/A'}<br>
          <strong>Duration:</strong> ${logInfo.duration || 'N/A'}<br><br>
          <strong>Details:</strong><br>
          ${logInfo.details || 'No additional details available.'}
        `
      }
    } catch (error) {
      console.error('Error showing log details:', error)
      modalContent.textContent = 'Error loading log details.'
    }
  }

  createLogDetailModal() {
    const modalHtml = `
      <div class="modal fade" id="logDetailModal" tabindex="-1" style="display: none;">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Audit Log Details</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">
              <pre id="logDetailContent">Loading...</pre>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    `
    
    document.body.insertAdjacentHTML('beforeend', modalHtml)
    const modal = document.getElementById('logDetailModal')
    
    // Bind close buttons
    modal.querySelectorAll('[data-bs-dismiss="modal"]').forEach(btn => {
      btn.addEventListener('click', () => this.closeModal(modal))
    })
    
    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeModal(modal)
      }
    })
    
    return modal
  }

  closeModal(modal) {
    modal.style.display = 'none'
    modal.classList.remove('show')
    document.body.classList.remove('modal-open')
  }

  // Utility methods
  getCSRFToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]')
    return tokenElement ? tokenElement.getAttribute('content') : ''
  }

  humanize(str) {
    if (!str) return ''
    return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getSyncStatusClass(status) {
    switch (status) {
      case 'running': return 'text-warning'
      case 'idle':
      case 'success': return 'text-success'
      case 'failed':
      case 'error': return 'text-danger'
      default: return 'text-secondary'
    }
  }

  getCircuitBreakerStatusClass(state) {
    switch (state) {
      case 'closed': return 'text-success'
      case 'half_open': return 'text-warning'
      case 'open': return 'text-danger'
      default: return 'text-secondary'
    }
  }

  getLockStatusClass(locked) {
    return locked ? 'text-warning' : 'text-success'
  }

  getErrorRateClass(rate) {
    if (rate <= 5) return 'text-success'
    if (rate <= 15) return 'text-warning'
    return 'text-danger'
  }

  showToast(message, type = 'info') {
    if (typeof toastr !== 'undefined') {
      // Use toastr if available
      if (type === 'error') {
        toastr.error(message)
      } else if (type === 'success') {
        toastr.success(message)
      } else {
        toastr.info(message)
      }
    } else {
      // Fallback to simple toast
      const toast = document.createElement('div')
      toast.className = `alert alert-${type === 'error' ? 'danger' : type === 'success' ? 'success' : 'info'} position-fixed`
      toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999; max-width: 300px;'
      toast.textContent = message
      
      document.body.appendChild(toast)
      
      setTimeout(() => {
        toast.remove()
      }, 5000)
    }
  }

  dispose() {
    this.stopAutoRefresh()
    
    // Destroy charts
    Object.values(this.charts).forEach(chart => {
      if (chart) {
        chart.destroy()
      }
    })
    this.charts = {}
  }
}

export default SiapMonitoring